/**
 * First-time profile setup component
 */
import { useState, useRef } from 'react';
import './ProfileSetup.css';

interface ProfileSetupProps {
  onComplete: (profile: { displayName: string; bio?: string; avatarDataUrl?: string }) => void;
  onSkip?: () => void;
  defaultDisplayName?: string;
}

export function ProfileSetup({ onComplete, onSkip, defaultDisplayName = '' }: ProfileSetupProps) {
  const [displayName, setDisplayName] = useState(defaultDisplayName);
  const [bio, setBio] = useState('');
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | undefined>();
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError(null);

    // Validate size (max 50KB for base64)
    if (file.size > 50 * 1024) {
      setAvatarError('Image must be under 50KB');
      return;
    }

    // Validate type
    if (!file.type.startsWith('image/')) {
      setAvatarError('File must be an image');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setAvatarDataUrl(dataUrl);
    };
    reader.onerror = () => {
      setAvatarError('Failed to read image');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!displayName.trim() || saving) return;
    console.log('[ProfileSetup] Saving profile...');
    setSaving(true);
    try {
      await onComplete({
        displayName: displayName.trim(),
        bio: bio.trim() || undefined,
        avatarDataUrl,
      });
    } catch (err) {
      console.error('[ProfileSetup] Error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      console.log('[ProfileSetup] Skipping...');
      onSkip();
    }
  };

  return (
    <div className="profile-setup-container">
      <div className="profile-setup-content">
        <h1 className="profile-setup-title">Create Your Profile</h1>
        <p className="profile-setup-tagline">Tell others who you are on the network</p>

        <div className="avatar-section">
          <div
            className="avatar-picker"
            onClick={() => fileInputRef.current?.click()}
          >
            {avatarDataUrl ? (
              <img src={avatarDataUrl} alt="Avatar preview" className="avatar-preview-img" />
            ) : (
              <div className="avatar-placeholder">
                <span className="avatar-placeholder-icon">+</span>
                <span className="avatar-placeholder-text">Add Photo</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            style={{ display: 'none' }}
          />
          {avatarError && <span className="avatar-error">{avatarError}</span>}
          <span className="avatar-hint">Max 50KB, click to change</span>
        </div>

        <div className="profile-form">
          <input
            type="text"
            className="profile-input display-name-input"
            placeholder="Display name *"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={50}
            autoFocus
          />

          <textarea
            className="profile-input bio-input"
            placeholder="Bio (optional)"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={200}
            rows={3}
          />
        </div>

        <div className="profile-actions">
          {onSkip && (
            <button
              className="skip-btn"
              onClick={handleSkip}
              onTouchEnd={(e) => { e.preventDefault(); handleSkip(); }}
              disabled={saving}
            >
              Skip for now
            </button>
          )}
          <button
            className="save-btn"
            onClick={handleSubmit}
            onTouchEnd={(e) => { e.preventDefault(); handleSubmit(); }}
            disabled={!displayName.trim() || saving}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        <p className="profile-note">You can update your profile anytime</p>
      </div>
    </div>
  );
}
